import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { PrimaryButton } from '../components/Button'
import Spinner from '../components/Spinner'
import { Container } from '../components/Animations'
import Modal from '../components/Modal'
import OneTimePassForm from '../components/OneTimePassForm'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import Radom, { User } from '../state/Radom'
import { errorToast, successToast } from '../util/Util'
import {
  MutedValue,
  SettingsCard,
  SettingsCardBody,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './Organization/SettingsLayout'

const SecuritySettings = observer((): React.ReactElement => {
  const [accountLoading, setAccountLoading] = useState(true)
  const [user, setUser] = useState<User>()
  const [is2faModalVisible, setIs2faModalVisible] = useState(false)

  const getUser = async (): Promise<void> => {
    setAccountLoading(true)
    try {
      const u = await Radom.getUser()
      setUser(u)
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    }
    setAccountLoading(false)
  }

  useEffect(() => {
    getUser()
  }, [])

  const is2faActive = user?.otpEnabled && user?.otpVerified

  return (
    <Container>
      <SettingsPage>

        <SettingsHero>
          <div>
            <SettingsTitle>Security</SettingsTitle>
            <SettingsSubtext>
              Manage your login identity and two-factor authentication for this account.
            </SettingsSubtext>
          </div>
          {!accountLoading && (
            <StatusPill $tone={is2faActive ? 'green' : 'amber'}>
              {is2faActive ? '2FA enabled' : '2FA recommended'}
            </StatusPill>
          )}
        </SettingsHero>

        <SettingsCard>
          {accountLoading
            ? <div style={{
              display: 'flex', width: '100%', height: 100, justifyContent: 'center', alignItems: 'center'
            }}>
              <Spinner />
            </div>
            : <SettingsCardBody>
              <SettingsRows>
                <SettingsRow>
                  <div>
                    <SettingsRowTitle>Sign-in identity</SettingsRowTitle>
                    <SettingsRowDescription>
                      The email address or wallet attached to this dashboard login.
                    </SettingsRowDescription>
                  </div>
                  <MutedValue>{user?.emailAddress || user?.evmAddress}</MutedValue>
                </SettingsRow>
                <SettingsRow>
                  <div>
                    <SettingsRowTitle>Two-factor authentication</SettingsRowTitle>
                    <SettingsRowDescription>
                      Require a one-time passcode when signing in to reduce account takeover risk.
                    </SettingsRowDescription>
                  </div>
                  {is2faActive
                    ? <ConfirmButtonModal
                      title='Disable'
                      description='Are you sure you want to disable two-factor authentication?'
                      action='Disable'
                      onConfirm={async () => {
                        try {
                          await Radom.disableOneTimePassword()
                          successToast('Two-factor authentication disabled')
                          getUser()
                        } catch (err: any) {
                          errorToast(err.response?.data?.error || err.message)
                        }
                      }}
                      ActionComponent={(props: any) =>
                        <PrimaryButton {...props}>
                          <span>Disable</span>
                        </PrimaryButton>
                      }
                    />
                    : <PrimaryButton onClick={() => setIs2faModalVisible(true)}>
                      Enable 2FA
                    </PrimaryButton>}
                </SettingsRow>
              </SettingsRows>
            </SettingsCardBody>}
        </SettingsCard>

        <Modal
          title='Two-factor authentication'
          visible={is2faModalVisible}
          onClose={() => setIs2faModalVisible(false)}
          innerStyles={{ width: 'min(520px, calc(100vw - 40px))' }}>
          <div style={{ maxWidth: 520 }}>
            <OneTimePassForm onTwoFactorSetSuccess={() => {
              successToast('Successfully set two-factor authentication')
              setIs2faModalVisible(false)
              getUser()
            }} />
          </div>
        </Modal>

      </SettingsPage>
    </Container>
  )
})

export default SecuritySettings
